import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AuthService from '../services/AuthService';
import DashboardService from '../services/DashboardService';
import { clearPersistedWorkspaceTabs } from '../components/workspaces/WorkspacePanel';

// Create the Auth Context
const AuthContext = createContext(null);

// Default connected system color
const DEFAULT_SYSTEM_COLOR = '#22c55e';

// localStorage key for connected system colors
const SYSTEM_COLORS_KEY = 'waide_system_colors';

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConnectedSystem, setSelectedConnectedSystem] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [systemColors, setSystemColors] = useState({});

  // Check authentication status on mount. Paints instantly from the cached
  // waide_user (no network round-trip, avoids a login-flicker), then
  // refreshes it from the server in the background -- see
  // AuthService.fetchCurrentUser()'s own doc comment for the real,
  // confirmed bug this second step exists to self-heal: a thin/corrupted
  // cached user (missing connectedSystems) used to persist forever with no
  // recovery path, silently wedging the app behind a permanent loading
  // spinner. A failed background refresh (offline, expired token) is
  // swallowed -- the instant cache-based paint above already stands, and
  // this is a best-effort correction, not a hard requirement to proceed.
  useEffect(() => {
    const checkAuth = async () => {
      if (AuthService.isAuthenticated()) {
        const storedUser = AuthService.getUser();
        setUser(storedUser);
        setIsAuthenticated(true);
        setIsLoading(false);
        try {
          const freshUser = await AuthService.fetchCurrentUser();
          setUser(freshUser);
        } catch (err) {
          console.warn('Background user profile refresh failed (using cached copy):', err);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Load persisted connected system selection on mount
  useEffect(() => {
    const saved = localStorage.getItem('current_selected_system');
    if (saved) {
      try {
        setSelectedConnectedSystem(JSON.parse(saved));
      } catch {
        localStorage.removeItem('current_selected_system');
      }
    }
  }, []);

  // Validate the persisted connected-system selection against the current
  // user, and auto-select when there's exactly one to choose from.
  //
  // current_selected_system is loaded from localStorage unconditionally on
  // mount (effect above), before this component knows which user is even
  // authenticated. localStorage isn't scoped per-user, so a value left over
  // from a DIFFERENT account previously used in the same browser (e.g. an
  // admin/demo account) survives into a brand new login and gets sent as an
  // explicit connectedSystemId override on every chat/wizard request.
  // ChatController now rejects an override that isn't in the current user's
  // own authorized connected systems (see its connectedSystemId resolution),
  // so this can no longer route a session to another tenant's data -- but
  // it would still silently misdirect wizard/chat context to a stale system
  // (its own request just gets bounced back to the user's real default)
  // without this check clearing the bad value client-side too. Confirmed in
  // production 2026-09-06: a brand-new signup's wizard executed against the
  // seeded Foysonis admin system (WHS000/FOYSONIS) instead of the new user's
  // own, correctly provisioned one, because a stale value from an earlier
  // session in that browser was never revalidated against this user's
  // actual access.
  useEffect(() => {
    if (!user) return;

    if (selectedConnectedSystem) {
      const stillValid = (user.connectedSystems || [])
          .some(cs => cs.id === selectedConnectedSystem.id);
      if (!stillValid) {
        setSelectedConnectedSystem(null);
        localStorage.removeItem('current_selected_system');
        return; // re-run on the next render with selectedConnectedSystem cleared
      }
    }

    if (user.connectedSystems?.length === 1 && !selectedConnectedSystem) {
      const singleSystem = user.connectedSystems[0];
      setSelectedConnectedSystem(singleSystem);
      localStorage.setItem('current_selected_system', JSON.stringify(singleSystem));
    }
  }, [user, selectedConnectedSystem]);

  // Load warehouses from API when authenticated
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const loadWarehouses = async () => {
      try {
        const token = AuthService.getAuthHeader();
        const res = await fetch('/api/v1/users/me/warehouses', {
          headers: { Authorization: token },
        });
        if (res.ok) {
          const data = await res.json();
          setWarehouses(data.warehouses || []);
          // Auto-select: restore from localStorage or use default
          const saved = localStorage.getItem('current_selected_warehouse');
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              const match = (data.warehouses || []).find(w => w.id === parsed.id);
              if (match) {
                setSelectedWarehouse(match);
                return;
              }
            } catch { /* ignore */ }
          }
          // Fallback: pick the default warehouse
          const defaultWh = (data.warehouses || []).find(w => w.isDefault);
          if (defaultWh) {
            setSelectedWarehouse(defaultWh);
            localStorage.setItem('current_selected_warehouse', JSON.stringify(defaultWh));
          } else if (data.warehouses?.length > 0) {
            setSelectedWarehouse(data.warehouses[0]);
            localStorage.setItem('current_selected_warehouse', JSON.stringify(data.warehouses[0]));
          }
        }
      } catch (err) {
        console.warn('Failed to load warehouses:', err);
      }
    };
    loadWarehouses();
  }, [isAuthenticated, user]);

  // Load persisted connected system colors on mount
  useEffect(() => {
    const savedColors = localStorage.getItem(SYSTEM_COLORS_KEY);
    if (savedColors) {
      try {
        setSystemColors(JSON.parse(savedColors));
      } catch {
        localStorage.removeItem(SYSTEM_COLORS_KEY);
      }
    }
  }, []);

  // Persist connected system colors when they change
  useEffect(() => {
    if (Object.keys(systemColors).length > 0) {
      localStorage.setItem(SYSTEM_COLORS_KEY, JSON.stringify(systemColors));
    }
  }, [systemColors]);

  // Logout function (defined early so it can be used in the refresh effect)
  const logout = useCallback(() => {
    // Clear the CURRENT user's open-workspace-tab cache before AuthService.logout()
    // wipes the stored user -- both clear calls scope their keys off
    // AuthService.getUser(), so they must run while that's still the outgoing
    // user, not after (when it'd resolve to 'anonymous' and clear the wrong
    // key). clearPersistedWorkspaceTabs() is the one that actually matters
    // for the platform-admin-workspace leak: WorkspacePanel's open tabs
    // (fastStudio.workspacePanel.tabs) are what get restored into the next
    // user's session, not DashboardService's (separate, chart-dashboard)
    // cache.
    DashboardService.clearCache();
    clearPersistedWorkspaceTabs();
    AuthService.logout();
    localStorage.removeItem('current_selected_system');
    localStorage.removeItem('current_selected_warehouse');
    localStorage.removeItem(SYSTEM_COLORS_KEY);
    setSelectedConnectedSystem(null);
    setSelectedWarehouse(null);
    setWarehouses([]);
    setSystemColors({});
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  // Listen for auth:unauthorized events from API services
  useEffect(() => {
    const handleUnauthorized = (event) => {
      console.warn('Auth: Unauthorized event received, logging out...', event.detail?.message);
      setError(event.detail?.message || 'Session expired. Please login again.');
      logout();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);

  // Function to set connected system (persists to localStorage)
  const selectConnectedSystem = useCallback((connectedSystem) => {
    setSelectedConnectedSystem(connectedSystem);
    localStorage.setItem('current_selected_system', JSON.stringify(connectedSystem));
  }, []);

  // Function to switch active warehouse (persists to localStorage + notifies backend)
  //
  // FH-96 FIXED 2026-10-12: this used to update local state/localStorage
  // BEFORE the backend call, and never checked response.ok at all (fetch
  // only throws on a true network failure -- a 403/500 was invisible, not
  // even logged). A rejected switch left the UI believing it was operating
  // against the new warehouse while the backend session still had the old
  // one active -- every subsequent API call (chat included) went out
  // scoped to a warehouse the backend didn't agree was active. Plausibly
  // the actual root mechanism behind FH-93's "Start a new conversation for
  // the selected facility" rejection, not just a similar-looking bug.
  // Local state now only updates AFTER the backend confirms success;
  // callers must await this and check for a thrown error.
  const selectWarehouse = useCallback(async (warehouse) => {
    const token = AuthService.getAuthHeader();
    const response = await fetch('/api/v1/users/me/active-warehouse', {
      method: 'PUT',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ warehouseId: warehouse.id }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.message || errorData.error || `Failed to switch warehouse (status ${response.status})`;
      setError(message);
      throw new Error(message);
    }

    setSelectedWarehouse(warehouse);
    localStorage.setItem('current_selected_warehouse', JSON.stringify(warehouse));
  }, []);

  // Helper to get color for a connected system (with default fallback)
  const getSystemColor = useCallback((systemId) => {
    return systemColors[systemId] || DEFAULT_SYSTEM_COLOR;
  }, [systemColors]);

  // Role utilities
  const userRoles = user?.roles || [];

  // Check if user has a specific role
  const hasRole = useCallback((role) => {
    return userRoles.includes(role);
  }, [userRoles]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roles) => {
    if (!roles || roles.length === 0) return true;
    return roles.some(role => userRoles.includes(role));
  }, [userRoles]);

  // Check if user can access a workspace based on allowed roles
  const canAccessWorkspace = useCallback((allowedRoles) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return allowedRoles.some(role => userRoles.includes(role));
  }, [userRoles]);

  // Token refresh effect - check every minute
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshInterval = setInterval(async () => {
      if (AuthService.tokenNeedsRefresh()) {
        try {
          await AuthService.refreshToken();
        } catch (err) {
          console.error('Token refresh failed:', err);
          logout();
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, logout]);

  // Login function. `tenantId` is only needed on a second call, once a first
  // call without it comes back requiring tenant selection (the same email
  // matches more than one tenant) -- see AuthService.login.
  // Deliberately does NOT touch the shared isLoading flag -- that flag also
  // drives App.js's full-page spinner, which REPLACES LoginPage (unmounts
  // it) while true. Toggling it around every login attempt was unmounting
  // LoginPage mid-request and remounting a fresh instance right after,
  // wiping any local state set from the response (e.g. tenantPickerOpen) --
  // this is why the tenant-selection dialog never appeared and the user
  // just landed back on a blank login page (found live 2026-09-21).
  // LoginPage already has its own local isSubmitting spinner on the submit
  // button; the global isLoading is for the one-time initial auth check on
  // mount, not per-login-attempt state.
  const login = useCallback(async (username, password, tenantId) => {
    setError(null);

    try {
      const result = await AuthService.login(username, password, tenantId);

      if (result.requiresTenantSelection) {
        return { success: false, requiresTenantSelection: true, tenantOptions: result.tenantOptions };
      }

      if (result.requiresPasswordChange) {
        return { success: false, requiresPasswordChange: true, resetToken: result.resetToken };
      }

      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);

        // Legacy silent-area-creation startup logic (StartupService) removed
        // 2026-09-22 -- superseded by FirstLoginFacilitySetupModal, which
        // enforces an explicit facility-type choice via the real
        // facility_setup_wizard template clone instead of silently creating
        // bare default areas/locations in the background.

        return { success: true };
      }
      throw new Error('Login failed');
    } catch (err) {
      const errorMessage = err.message || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Get auth header for API calls
  const getAuthHeader = useCallback(() => {
    return AuthService.getAuthHeader();
  }, []);

  // Re-reads the user object AuthService just wrote to localStorage (e.g.
  // after AuthService.acceptTerms()) into this context's own state, so
  // consumers gating on user.termsAcceptedAt re-render immediately instead
  // of needing a fresh login/page reload.
  const refreshUserFromStorage = useCallback(() => {
    setUser(AuthService.getUser());
  }, []);

  // Context value
  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError,
    getAuthHeader,
    refreshUserFromStorage,
    selectedConnectedSystem,
    selectConnectedSystem,
    needsSystemSelection: user?.connectedSystems?.length > 1 && !selectedConnectedSystem,
    // Warehouse context
    warehouses,
    selectedWarehouse,
    selectWarehouse,
    systemColors,
    setSystemColors,
    getSystemColor,
    // Role utilities
    userRoles,
    hasRole,
    hasAnyRole,
    canAccessWorkspace,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
